import { pull } from "lodash-es";
import { mix } from "polished";
import { type FC } from "react";
import { Field, type WrappedFieldProps } from "redux-form";
import { type BaseFieldProps } from "redux-form/lib/Field";
import { ColorButton, SymbolIcon } from "widgets";
import { normalize } from "widgets/utils";

import { FormInputGroup } from "@/ui-modules/core/form/layout/FormGroup.js";
import { FormInputWidget } from "@/ui-modules/core/form/layout/FormInputWidget.js";
import { FormLabel } from "@/ui-modules/core/form/layout/FormLabel.js";

import { type ColorScheme } from "../../graphql.js";
import CheckboxField, { CheckboxReduxFormField } from "../core/redux-form/CheckboxField.js";
import InputField, { InputReduxFormField } from "../core/redux-form/InputField.js";
import style from "./styles/ColorScheme.module.scss";

// Keep the values as the key for TS interface definition in form components
enum DEFAULT_COLOR_SCHEME_FORM_FIELDS {
  SCALE = "SCALE",
  MIN = "MIN",
  MAX = "MAX",
  LOG = "LOG",
}

// const DEFAULT_COLORS = ["#91bfdb", "#ffffbf", "#fc8d59"];
// const DEFAULT_COLORS = ["#69b34c", "#fab733", "#ff000d"];
const DEFAULT_COLORS = ["#ff000d", "#00ff0d"];

export interface DefaultColorSchemeFormData {
  [DEFAULT_COLOR_SCHEME_FORM_FIELDS.SCALE]: ColorScheme;
  [DEFAULT_COLOR_SCHEME_FORM_FIELDS.MIN]: string;
  [DEFAULT_COLOR_SCHEME_FORM_FIELDS.MAX]: string;
  [DEFAULT_COLOR_SCHEME_FORM_FIELDS.LOG]: boolean;
}

export type ColorSchemeFormFields<P = {}> = {
  readonly [Property in keyof typeof DEFAULT_COLOR_SCHEME_FORM_FIELDS]: {
    readonly name: string;
    validate?: BaseFieldProps<P>["validate"];
  };
};

export interface ColorSchemeConfigurationFieldsProps {
  fields: ColorSchemeFormFields;
}

// ! We need to keep the possibility to have 3 colors for backward compatibility of saved views. 2 colors is usual so it will never be an issue.
const ColorSchemeConfigurationFields: FC<ColorSchemeConfigurationFieldsProps> = (props) => {
  return (
    <FormInputGroup>
      <FormLabel>Color Scale</FormLabel>
      <div style={{ display: "flex", columnGap: 10, alignItems: "center" }}>
        <div style={{ width: "100px" }}>
          <InputReduxFormField
            name={props.fields.MIN.name}
            componentClass="input"
            type="number"
            placeholder="min"
            component={InputField}
            validate={props.fields.MIN.validate}
          />
        </div>
        <div style={{ width: "300px" }}>
          <Field name={props.fields.SCALE.name} validate={props.fields.SCALE.validate} component={Gradient} />
        </div>
        <div style={{ width: "100px" }}>
          <InputReduxFormField
            name={props.fields.MAX.name}
            componentClass="input"
            type="number"
            placeholder="max"
            component={InputField}
            validate={props.fields.MAX.validate}
          />
        </div>
        <CheckboxReduxFormField name={props.fields.LOG.name} label="Log" disabled={false} component={CheckboxField} />
      </div>
    </FormInputGroup>
  );
};

const Gradient: FC<Partial<WrappedFieldProps>> = (props) => {
  // Written in capitals because we must NOT mutate this variable. Const in JavaScript are not immutable!
  // 0, "", undefined, etc... are not valid values
  const COLORS: string[] = props.input?.value ? (props.input?.value?.colors ?? DEFAULT_COLORS) : DEFAULT_COLORS;
  const onChange = props.input?.onChange;
  const length = COLORS.length;
  const position = (index: number) => Math.round((index / (length - 1)) * 100);

  const cssGradient = () => {
    const gradientStrings: string[] = [];

    for (const [index, color] of COLORS.entries()) {
      gradientStrings.push(`${color} ${position(index)}%`);
    }

    return gradientStrings.join(", ");
  };

  return (
    <FormInputGroup>
      <FormInputWidget>
        <div style={{ display: "inline" }}>
          <div className={style.gradient_box} style={{ background: `linear-gradient(to right, ${cssGradient()})` }}>
            {COLORS.map((color, index) => (
              <div
                key={`color-scheme-field-${index}`}
                className={style.color_picker}
                style={{
                  left: `${position(index)}%`,
                  // Design: Just better place the picker regarding the border of the whole gradient box. Supports more than 3 if needed.
                  transform: `translate(calc(-50% - ${normalize(index, 0, length - 1, -3, 3)}px), -50%)`,
                }}
              >
                <ColorButton
                  defaultColor={color}
                  onApply={(color) => {
                    const clone = [...COLORS];
                    clone[index] = color;
                    onChange?.({ colors: clone });
                  }}
                  size="tiny"
                />
                {index > 0 && index < length - 1 && (
                  <div
                    className={style.remove_color}
                    onClick={() => {
                      const clone = [...COLORS];
                      pull(clone, color);
                      onChange?.({ colors: clone });
                    }}
                  >
                    <SymbolIcon icon="close" size={12} />
                  </div>
                )}
              </div>
            ))}
            {length === 2 && (
              <div
                className={style.new_color}
                onClick={() => {
                  // Only allow a third color in the middle for the moment
                  onChange?.({ colors: [COLORS[0], mix(0.5, COLORS[0], COLORS[1]), COLORS[1]] });
                }}
              >
                <SymbolIcon icon="add" />
              </div>
            )}
          </div>
        </div>
      </FormInputWidget>
    </FormInputGroup>
  );
};

export { ColorSchemeConfigurationFields, DEFAULT_COLOR_SCHEME_FORM_FIELDS, DEFAULT_COLORS };
